import { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import companiesCollection from 'modules/core/store/collections/companies'

import { useCompanies } from 'modules/factorial_id/hooks/useCompanies'
import { buildRedirectUrl } from 'modules/factorial_id/lib/api_client'
import {
  getCompanies,
  selectCompany as selectCompanyApi,
} from 'modules/factorial_id/lib/api_client'

/**
 * Custom hook that returns a company switch handler
 *
 * This hook handles both Factorial ID and monolith company switching flows:
 * 1. Determines if the target company uses Factorial ID based on the fid_enabled flag
 * 2. If enabled, uses Factorial ID flow (fetches companies, calls selection API, handles redirects)
 * 3. If disabled or on error, falls back to monolith flow
 * 4. Handles all redirects and page reloads internally
 *
 * @returns Handler function that takes a company ID and can be passed directly to onChange
 *
 * @example
 * ```tsx
 * const switchCompany = useSwitchCompany()
 * return <SidebarHeader onChange={switchCompany} />
 * ```
 */
export const useSwitchCompany = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { email } = useCurrentSession()
  const { companies } = useCompanies()

  return useCallback(
    async (companyId: string) => {
      const targetCompany = companies.find((c) => c.id === companyId)
      const shouldUseFactorialId = targetCompany?.fid_enabled ?? false

      let shouldFallbackToMonolith = false

      if (shouldUseFactorialId && email) {
        try {
          const fidCompanies = await getCompanies()

          const fidCompany = fidCompanies.find((c) => c.external_id === companyId)

          if (fidCompany) {
            const response = await selectCompanyApi(fidCompany.id)

            if (response.next) {
              // Shortcut for the redirect challenge, that just involves
              // navigating to another page and is guaranteed to not have follow
              // up challanges. We perform the redirect here instead of going to
              // Factorial ID.
              if (response.next.type == 'redirect') {
                window.location.href = response.next.to
              } else {
                const redirectUrl = buildRedirectUrl(email, response)
                window.location.href = redirectUrl
              }
              return
            }

            if (response.success) {
              window.location.replace(window.location.origin)
              return
            }
          }

          shouldFallbackToMonolith = true
        } catch (error) {
          shouldFallbackToMonolith = true
        }
      } else {
        shouldFallbackToMonolith = true
      }

      // Monolith fallback
      if (shouldFallbackToMonolith) {
        const selectedCompany = companiesCollection.get(parseInt(companyId, 10))
        await selectedCompany?.rpc('select_current')

        const returnTo = location.state?.returnTo
        if (returnTo) {
          navigate(returnTo)
        } else {
          window.location.replace(window.location.origin)
        }
      }
    },
    [companies, email, location, navigate]
  )
}
